import { forwardRef, useCallback, useRef, useState } from "react";
import type { ComponentPropsWithoutRef, CSSProperties, JSX } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useQuery } from "@tanstack/react-query";

import { DashboardWidgetDefinition } from "src/types/dashboard";
import DashboardWidgetContent from "src/components/builder/DashboardWidgetContent";
import { getDashboardWidgetDataApi } from "src/api/dashboardApi";
import { openTableMenu } from "src/charts/tableMenuRegistry";
import { beginKpiHover, endKpiHover, track } from "src/telemetry";

type WidgetRendererProps = Omit<ComponentPropsWithoutRef<"div">, "children"> & {
  widget: DashboardWidgetDefinition;
  dashboard_name: string;
  appliedFilters: Record<string, unknown>;
};

type WidgetCssVars = CSSProperties & Record<`--${string}`, string>;

const WidgetTooltip = forwardRef<HTMLDivElement, { description: string }>(
  (props, ref): JSX.Element => {
    return (
      <span
        ref={ref}
        className="d-inline-flex align-items-center text-muted"
        role="button"
        tabIndex={0}
        aria-label={props.description ?? "Widget description"}
        {...props}
      >
        <i className="bi bi-question-circle" />
      </span>
    );
  },
);

const WidgetRenderer = forwardRef<HTMLDivElement, WidgetRendererProps>(
  (
    { widget, className, style, dashboard_name, appliedFilters, ...rest },
    ref,
  ): JSX.Element => {
    const { x, y, w, h } = widget.grid;
    const { min_w, min_h, max_w, max_h } = widget.grid_constraints;
    const headerRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);

    // custom_table/average_bar/deviation_bar/cards all self-fetch (their own
    // Top-N selectors need to refetch independently — see the `enabled`
    // clause below), so the outer query's rawResponse never carries their
    // has_download_options. Each widget reports it back up through this once
    // its own fetch resolves, so the header button can be gated the same way
    // as the other download-only types. Only one of these chart types is
    // ever active per WidgetRenderer instance, so a single piece of state
    // (rather than one per chart type) is enough.
    const [selfFetchHasDownloadOptions, setSelfFetchHasDownloadOptions] =
      useState(false);
    const handleSelfFetchDownloadOptionsChange = useCallback(
      (has: boolean) => setSelfFetchHasDownloadOptions(has),
      [],
    );

    const { data, isLoading, isError } = useQuery({
      queryKey: ["widget", dashboard_name, widget.id, appliedFilters],
      queryFn: async ({ signal }) =>
        getDashboardWidgetDataApi(
          dashboard_name,
          String(widget.id),
          { filters: appliedFilters },
          { signal },
        ),
      enabled:
        !!dashboard_name &&
        Object.keys(appliedFilters).length > 0 &&
        widget.chart_type !== "special_quadrant_chart" &&
        widget.chart_type !== "tiered_bar" &&
        // these fetch internally so their Top-N selectors can refetch
        widget.chart_type !== "custom_lines" &&
        widget.chart_type !== "custom_table" &&
        widget.chart_type !== "deviation_bar" &&
        widget.chart_type !== "average_bar" &&
        widget.chart_type !== "cards" &&
        // bubble_quadrant_chart self-fetches too (its Top-N select and
        // threshold sliders refetch on their own). Leaving it out of this list
        // meant every load fired the same request twice — once here, once in
        // the widget — and the two queryKeys differ by the widget's extra
        // widget_filters entry, so react-query could not dedupe them.
        widget.chart_type !== "bubble_quadrant_chart",
      staleTime: 0,
      gcTime: 0,
    });

    const rawResponse = data as
      | {
          data?: unknown;
          metadata?: Record<string, unknown>;
          has_download_options?: boolean;
          download_options?: { csv?: boolean; case_data?: boolean };
        }
      | undefined;
    const widgetData =
      widget.chart_type === "quadrant_table"
        ? {
            practices: Array.isArray(rawResponse?.data) ? rawResponse.data : [],
            tier_rules: rawResponse?.metadata?.tier_rules ?? {},
          }
        : widget.chart_type === "donut_pie" ||
            widget.chart_type === "info_bar" ||
            widget.chart_type === "bar_drilldown" ||
            widget.chart_type === "bar_drilldown_custom" ||
            widget.chart_type === "sentiment_split" ||
            widget.chart_type === "sentiment_scatter" ||
            widget.chart_type === "sentiment_histogram" ||
            // data_table/custom_bar/single_stack_bar have no self-fetch of
            // their own, so has_download_options/download_options (siblings
            // of .data, not under .metadata) would otherwise be dropped by
            // the rawResponse?.data fallback below, and the widget itself
            // would never see them (button would show via rawResponse
            // above, but the widget's own DownloadOptionsMenu would never
            // register — "button appears, dropdown never opens").
            widget.chart_type === "data_table" ||
            widget.chart_type === "custom_bar" ||
            // sub_bar reads metadata.min_decided_for_rate to explain its
            // suppressed rates, and its own download_options.
            widget.chart_type === "sub_bar" ||
            widget.chart_type === "single_stack_bar"
          ? // keep the envelope — these widgets read metadata too
            // (donut_pie: totals; info_bar: insufficient-data breakout;
            // bar_drilldown/bar_drilldown_custom: headline/status/technical
            // detail rows; sentiment_split: total_scored_calls caption;
            // sentiment_scatter/sentiment_histogram: backend axis labels, call
            // totals, and the truncated / clamped_calls flags)
            rawResponse
          : (rawResponse?.data ?? rawResponse);

    const widgetStyle: WidgetCssVars = {
      width: "100%",
      height: "100%",
      "--widget-x": String(x),
      "--widget-y": String(y),
      "--widget-w": String(w),
      "--widget-h": String(h),
      "--widget-min-w": String(min_w),
      "--widget-min-h": String(min_h),
      "--widget-max-w": String(max_w),
      "--widget-max-h": String(max_h),
    };

    const mergedStyle: WidgetCssVars = {
      ...widgetStyle,
      ...(style as CSSProperties),
    };

    const mergedClassName = className
      ? `card shadow-sm ${className}`
      : "card shadow-sm";

    const openExportingMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
      // The 3-dots button itself. Tracked HERE rather than on the individual menu
      // items because this is the only layer with widget and dashboard identity —
      // the Highcharts menu builder only has the chart. One event per menu open
      // answers "which widgets do people poke at" without a row per sub-action.
      track("widget.menu_opened", {
        widgetCode: String(widget.widget_template_id),
        dashboardCode: dashboard_name,
      });

      const containerId = `${widget.id}`;
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = { x: rect.right, y: rect.bottom };

      // openTableMenu is a no-op (returns false) when not in table mode.
      // In table mode it calls ChartTableView's registered setter directly — no DOM guessing.
      if (openTableMenu(containerId, pos)) return;

      // Normal chart mode: fire the Highcharts hamburger menu.
      const container = document.getElementById(containerId);
      const exportBtn = container?.querySelector(
        ".highcharts-contextbutton",
      ) as HTMLElement;
      ((exportBtn as any)?.hcEvents?.click?.[0] as any)?.fn();
    };

    // Chart types with no context menu (pure display widgets, no chart/conversion).
    // kpi and sparkle now register a download menu (KpiExportMenu) under the
    // widget id, so the header hamburger works for them via openTableMenu.
    // special_quadrant_chart registers its own menu the same way
    // (PracticeDetailsMenu) — its one item is always client-side available
    // (no has_download_options gate needed), so it's excluded from here too.
    const NO_MENU_TYPES = new Set([
      "kpi_with_detail",
      "tiered_bar",
      // no embedded Highcharts instance to proxy the shell's hamburger into
      "bar_drilldown_custom",
    ]);

    // These chart types have NO menu of their own (no native Highcharts menu,
    // no client-side export items) — the only thing the header button could
    // ever open for them is DownloadOptionsMenu. So unlike bar/kpi/pie/etc.,
    // the button itself must only appear when the backend actually declared
    // has_download_options, otherwise it's a hamburger icon that opens nothing.
    // custom_bar/sentiment_split/single_stack_bar all use the outer query
    // (not self-fetch — see the `enabled` clause above), so they belong here
    // rather than in SELF_FETCH_DOWNLOAD_TYPES below. single_stack_bar has a
    // real Highcharts instance but renders it with exporting disabled (see
    // SingleStackBarWidget.tsx), so it's in the same boat as the others —
    // no native menu of its own to fall back to.
    const DOWNLOAD_ONLY_MENU_TYPES = new Set([
      "data_table",
      "info_bar",
      "donut_pie",
      "custom_bar",
      "sub_bar",
      "sentiment_split",
      "single_stack_bar",
    ]);

    // Same as DOWNLOAD_ONLY_MENU_TYPES, but these self-fetch (their own
    // Top-N selectors refetch independently of appliedFilters — see the
    // `enabled` clause above), so rawResponse never carries their
    // has_download_options at this level. Gated on selfFetchHasDownloadOptions
    // instead, which the active widget reports back up once it knows.
    const SELF_FETCH_DOWNLOAD_TYPES = new Set([
      "custom_table",
      "average_bar",
      "deviation_bar",
      "cards",
      "bubble_quadrant_chart",
      "custom_lines",
    ]);

    const showMenuButton = SELF_FETCH_DOWNLOAD_TYPES.has(widget.chart_type)
      ? selfFetchHasDownloadOptions
      : DOWNLOAD_ONLY_MENU_TYPES.has(widget.chart_type)
        ? !!rawResponse?.has_download_options
        : !NO_MENU_TYPES.has(widget.chart_type);

    // The browser only has the numeric template id; the backend resolves it to the
    // readable widget code (`case_volume`) and names the activity after it, so a
    // stored row identifies the KPI on its own. Sent as a string because the
    // telemetry payload treats widget_code as an opaque identifier.
    const widgetCode = String(widget.widget_template_id);

    return (
      <div
        ref={ref}
        className={mergedClassName}
        style={mergedStyle}
        // KPI utilization. Only hovers longer than the dwell threshold count, and
        // repeats on the same widget are coalesced into one event per flush — a
        // glance across 15 widgets would otherwise dominate the whole dataset.
        onMouseEnter={() => beginKpiHover(widgetCode, dashboard_name)}
        onMouseLeave={() => endKpiHover(widgetCode)}
        {...rest}
      >
        <div
          ref={headerRef}
          className="card-header d-flex align-items-start justify-content-between gap-2"
        >
          <h4 className="mb-0">{widget.title}</h4>
          {/* flex + shrink-0 keeps the buttons on one line however narrow the
              widget gets (a floated block let the icons wrap underneath) */}
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            {showMenuButton ? (
              <button onClick={openExportingMenu} className="btn p-0">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <rect
                    x="3"
                    y="5"
                    width="18"
                    height="3"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="3"
                    rx="1"
                    fill="currentColor"
                  />
                  <rect
                    x="3"
                    y="17"
                    width="18"
                    height="3"
                    rx="1"
                    fill="currentColor"
                  />
                </svg>
              </button>
            ) : null}
            <OverlayTrigger
              // "auto" (not "top") is what actually enables Popper's flip
              // modifier — react-bootstrap only turns flip on when the
              // placement string contains "auto" (OverlayTrigger.js), and
              // Popper's real "auto" placement then picks whichever of
              // top/right/bottom/left has the most room for this tooltip,
              // instead of being pinned above the icon and clipped when
              // there's no space there.
              placement="auto"
              overlay={
                <Tooltip
                  id={`widget-description-${String(widget.id ?? widget.widget_template_id)}`}
                  // Bootstrap's default tooltip z-index (1080) sits well
                  // below the sticky header's (.navbar { z-index: 9999 } in
                  // generic.scss), so a tooltip near the top of the page
                  // rendered underneath it instead of on top.
                  style={{ zIndex: 10000 }}
                >
                  {widget.description ?? "No description available"}
                </Tooltip>
              }
            >
              <WidgetTooltip
                description={widget.description ?? "No description available"}
              />
            </OverlayTrigger>
          </div>
        </div>

        <div
          ref={bodyRef}
          className={`card-body d-flex flex-fill flex-column overflow-auto ${
            ["data_table"].includes(widget.chart_type)
              ? "px-0 pt-2 justify-content-center"
              : ["special_quadrant_chart", "tiered_bar"].includes(
                    widget.chart_type,
                  )
                ? "p-0"
                : // sub_bar lists top-align: with justify-content-center a
                  // single payer (or a narrow insurance-type filter) floated
                  // into the middle of the card while the legend and filter
                  // drifted with it.
                  ["sparkle", "sub_bar"].includes(widget.chart_type)
                  ? ""
                  : "justify-content-center"
          }`}
        >
          <DashboardWidgetContent
            chartType={widget.chart_type}
            title={widget.title}
            description={widget.description}
            data={widgetData}
            isLoading={isLoading}
            hasError={isError && !isLoading ? true : false}
            chartSpec={widget.chart_spec}
            dashboardName={dashboard_name}
            widgetTemplateId={String(widget.id)}
            appliedFilters={appliedFilters}
            hasDownloadOptions={rawResponse?.has_download_options}
            downloadOptions={rawResponse?.download_options}
            onDownloadOptionsChange={handleSelfFetchDownloadOptionsChange}
          />
        </div>
      </div>
    );
  },
);

WidgetRenderer.displayName = "WidgetRenderer";

export default WidgetRenderer;
